import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLORIDA_MAP = "https://images.pexels.com/photos/5177211/pexels-photo-5177211.jpeg";

const serviceAreas = [
  'Boca Raton',
  'Delray Beach',
  'Boynton Beach',
  'Palm Beach Gardens',
  'West Palm Beach',
  'Jupiter',
  'Wellington',
  'Lake Worth',
  'Coral Springs',
  'Pompano Beach',
  'Fort Lauderdale',
  'Deerfield Beach',
];

const faqs = [
  {
    question: 'What areas do you serve?',
    answer: 'Senior Care Solutions provides home healthcare services throughout South Florida, including Palm Beach County, Broward County, and surrounding areas. Our main office is located in Boca Raton, and we serve communities from Jupiter to Fort Lauderdale.',
  },
  {
    question: 'Is Senior Care Solutions Medicare certified?',
    answer: 'Yes, Senior Care Solutions is fully Medicare certified and CHAP/ACHC accredited. This means many of our skilled nursing and therapy services may be covered by Medicare. Our team will help you understand your coverage options and benefits.',
  },
  {
    question: 'What types of services do you offer?',
    answer: 'We offer a comprehensive range of home healthcare services including skilled nursing, physical therapy, occupational therapy, speech therapy, cardiac and pulmonary care, wound care management, infusion therapy, and home health aide services. Our multi-disciplined team creates personalized care plans for each patient.',
  },
  {
    question: 'How do I get started with home healthcare?',
    answer: "Getting started is easy. You can call us at (561) 299-0100 or fill out our contact form. We'll discuss your needs, verify your insurance coverage, and schedule a free consultation. Our team will then work with your physician to develop a personalized care plan.",
  },
  {
    question: 'Do I need a doctor\'s referral?',
    answer: 'For Medicare-covered services, a physician\'s order is required. However, our team can help coordinate with your doctor to obtain the necessary referral. For private pay services, we can discuss options directly with you.',
  },
  {
    question: 'Is 24/7 support available?',
    answer: 'Yes! We provide 24/7 clinical on-call support. Our patients and their families always have access to a qualified healthcare professional who can answer questions and address concerns, day or night.',
  },
  {
    question: 'What insurance do you accept?',
    answer: 'We accept Medicare, Medicaid, most private insurance plans, and private pay. Our billing team will verify your coverage and help you understand your benefits before services begin.',
  },
  {
    question: 'How long will I need home healthcare services?',
    answer: 'The duration of care depends on your individual needs and goals. Some patients need short-term care after surgery or hospitalization, while others benefit from ongoing support for chronic conditions. We regularly evaluate your progress and adjust the care plan accordingly.',
  },
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#000080]" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-[#000080] to-[#1E3A8A]"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#34D399] font-semibold mb-4">CONTACT US</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-white/80">
              Ready to learn more about how we can help? Contact us today for a free 
              consultation. Our caring team is here to answer your questions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">
                Request a Free Consultation
              </h2>
              <p className="text-[#334155] mb-8">
                Fill out the form below and one of our care coordinators will contact 
                you within 24 hours to discuss your needs.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-[#0F172A] font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 form-input border-slate-200 focus:border-[#00A878] rounded-lg py-6"
                    placeholder="Enter your full name"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#0F172A] font-medium">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 form-input border-slate-200 focus:border-[#00A878] rounded-lg py-6"
                    placeholder="Enter your email"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#0F172A] font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 form-input border-slate-200 focus:border-[#00A878] rounded-lg py-6"
                    placeholder="(555) 555-5555"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#0F172A] font-medium">
                    How Can We Help? *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="mt-2 form-input border-slate-200 focus:border-[#00A878] rounded-lg resize-none"
                    placeholder="Tell us about your care needs..."
                    data-testid="input-message"
                  />
                </div>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-3 p-4 bg-[#ECFDF5] rounded-lg" data-testid="success-message">
                    <CheckCircle2 className="w-6 h-6 text-[#00A878]" />
                    <p className="text-[#065F46]">
                      Thank you! We've received your message and will contact you within 24 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg" data-testid="error-message">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                    <p className="text-red-700">
                      Something went wrong. Please try again or call us directly.
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-[#00A878] hover:bg-[#065F46] text-white rounded-full py-6 text-lg font-semibold"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner"></div>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </span>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">
                Contact Information
              </h2>
              <p className="text-[#334155] mb-8">
                Prefer to reach us directly? Here's how you can get in touch.
              </p>

              <div className="space-y-6">
                <Card className="border border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#00A878]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A] mb-1">Phone</h3>
                      <a 
                        href="tel:+15612990100" 
                        className="text-[#00A878] font-semibold text-lg hover:underline"
                        data-testid="contact-phone"
                      >
                        (561) 299-0100
                      </a>
                      <p className="text-sm text-[#334155] mt-1">24/7 Clinical Support Available</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#00A878]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A] mb-1">Email</h3>
                      <a 
                        href="mailto:info@seniorcaresolutionsfl.com" 
                        className="text-[#00A878] hover:underline"
                        data-testid="contact-email"
                      >
                        info@seniorcaresolutionsfl.com
                      </a>
                      <p className="text-sm text-[#334155] mt-1">We respond within 24 hours</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#00A878]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A] mb-1">Office Address</h3>
                      <p className="text-[#334155]">
                        7000 W. Palmetto Park Road<br />
                        Suite 210<br />
                        Boca Raton, FL 33433
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#00A878]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A] mb-1">Office Hours</h3>
                      <p className="text-[#334155]">
                        Monday - Friday: 8:00 AM - 5:00 PM<br />
                        <span className="text-[#00A878] font-medium">24/7 Clinical On-Call Support</span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Map */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="service-areas">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-[#00A878] font-semibold mb-4">SERVICE AREAS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Serving South Florida
            </h2>
            <p className="text-lg text-[#334155]">
              We proudly provide home healthcare services throughout Palm Beach and 
              Broward Counties. Contact us to confirm service availability in your area.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-[#00A878]/10 to-[#000080]/10 rounded-3xl p-8">
                {/* Florida Map SVG */}
                <svg 
                  viewBox="0 0 400 500" 
                  className="w-full h-auto"
                  data-testid="florida-map"
                >
                  {/* Simplified Florida shape */}
                  <path
                    d="M80,50 L320,50 L340,80 L350,120 L340,180 L320,240 L300,300 L280,350 L260,400 L240,430 L220,450 L200,460 L180,450 L170,420 L160,380 L140,340 L120,290 L100,240 L80,190 L70,140 L75,90 Z"
                    fill="#ECFDF5"
                    stroke="#00A878"
                    strokeWidth="3"
                  />
                  {/* Service area highlight (South Florida) */}
                  <path
                    d="M140,340 L280,350 L260,400 L240,430 L220,450 L200,460 L180,450 L170,420 L160,380 Z"
                    fill="#00A878"
                    fillOpacity="0.3"
                    stroke="#00A878"
                    strokeWidth="2"
                  />
                  {/* Boca Raton marker */}
                  <circle cx="230" cy="390" r="12" fill="#000080" />
                  <circle cx="230" cy="390" r="8" fill="#00A878" />
                  {/* Other service area dots */}
                  <circle cx="200" cy="360" r="6" fill="#00A878" className="animate-pulse" />
                  <circle cx="250" cy="370" r="6" fill="#00A878" className="animate-pulse" />
                  <circle cx="210" cy="400" r="6" fill="#00A878" className="animate-pulse" />
                  <circle cx="180" cy="380" r="6" fill="#00A878" className="animate-pulse" />
                  <circle cx="240" cy="410" r="6" fill="#00A878" className="animate-pulse" />
                  {/* Label */}
                  <text x="200" cy="480" textAnchor="middle" className="text-sm fill-[#334155] font-semibold">
                    South Florida Service Area
                  </text>
                </svg>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6">
                Communities We Serve
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {serviceAreas.map((area, index) => (
                  <div 
                    key={area}
                    className="flex items-center gap-2"
                    data-testid={`service-area-${index}`}
                  >
                    <div className="w-2 h-2 rounded-full bg-[#00A878]"></div>
                    <span className="text-[#334155]">{area}</span>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-[#334155]">
                Don't see your area listed? Contact us to check if we serve your location.
              </p>
              <a href="tel:+15612990100" className="inline-block mt-4">
                <Button 
                  className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-6"
                  data-testid="check-coverage-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Check Coverage
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-[#00A878] font-semibold mb-4">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#334155]">
              Find answers to common questions about our home healthcare services.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-[#F8FAFC] rounded-xl border-none px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left text-[#0F172A] font-semibold hover:text-[#00A878] py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#334155] pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#000080]" data-testid="contact-final-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Start Your Care Journey Today
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Our compassionate team is ready to help you or your loved one receive 
            the best care at home. Call us now or request a consultation.
          </p>
          <a href="tel:+15612990100" data-testid="final-cta-phone">
            <Button 
              size="lg" 
              className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-10 py-6 text-xl font-semibold"
            >
              <Phone className="w-6 h-6 mr-3" />
              Call (561) 299-0100
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
